import React from "react";
import scriptLoader from "react-async-script-loader";
import config from "../config";
import _ from "lodash";
import axios from "axios";
import { withTranslation } from "react-i18next";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Icon from "@material-ui/core/Icon";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { withStyles } from "@material-ui/core/styles";

import SimpleDialog from "./SimpleDialog";

// ~400km around Łódź. Places API (New) caps circle bias radius at 50km,
// so a rectangle is used to keep the previous bias area.
const LOCATION_BIAS = {
  south: 48.16,
  west: 13.65,
  north: 55.36,
  east: 25.26,
};

class AddressInput extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      address: "",
      addressObj: {},
      addressName: "",
      ready: false,
      dragging: false,
      error: false,
      simpleDialogOpen: false,
      suggestions: [],
      activeSuggestion: 0,
      loading: false,
    };
    this.sessionToken = null;
    this.requestId = 0;
    this.fetchSuggestions = _.debounce(this.fetchSuggestions, 500);
  }

  componentWillUnmount() {
    this.fetchSuggestions.cancel();
  }

  loadPlacesLibrary = () => {
    if (!this.placesLibrary) {
      this.placesLibrary = window.google.maps.importLibrary("places");
    }
    return this.placesLibrary;
  };

  componentWillReceiveProps({
    isScriptLoaded,
    isScriptLoadSucceed,
    selectedAddress,
    selectedAddressObj,
  }) {
    if (isScriptLoaded && !this.props.isScriptLoaded) {
      // load finished
      if (isScriptLoadSucceed) {
        this.setState({ ready: true });
      }
    }

    if (selectedAddress)
      this.setState({
        address: selectedAddress,
        addressObj: selectedAddressObj,
      });
  }

  componentDidMount() {
    const { isScriptLoaded, isScriptLoadSucceed } = this.props;
    if (isScriptLoaded && isScriptLoadSucceed) {
      this.setState({ ready: true });
    }
  }

  handleChange = (e) => {
    const address = e.target.value;
    if (address.includes("}")) return false;
    this.setState({ address, addressObj: {} });
    this.props.onSelect({});

    if (address.length > 2) {
      this.fetchSuggestions(address);
    } else {
      this.fetchSuggestions.cancel();
      this.requestId++;
      this.setState({ suggestions: [], loading: false });
    }
  };

  fetchSuggestions = async (input) => {
    const requestId = ++this.requestId;
    this.setState({ loading: true });

    try {
      const {
        AutocompleteSuggestion,
        AutocompleteSessionToken,
      } = await this.loadPlacesLibrary();

      if (!this.sessionToken) {
        this.sessionToken = new AutocompleteSessionToken();
      }

      const {
        suggestions,
      } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: this.sessionToken,
        locationBias: LOCATION_BIAS,
      });

      if (requestId !== this.requestId) return;

      this.setState({
        suggestions: suggestions
          .map((suggestion) => suggestion.placePrediction)
          .filter(Boolean),
        activeSuggestion: 0,
        loading: false,
      });
    } catch (err) {
      console.log(err);
      if (requestId === this.requestId) {
        this.setState({ suggestions: [], loading: false });
      }
    }
  };

  handleKeyDown = (e) => {
    const { suggestions, activeSuggestion } = this.state;
    if (!suggestions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.setState({
        activeSuggestion: (activeSuggestion + 1) % suggestions.length,
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.setState({
        activeSuggestion:
          (activeSuggestion - 1 + suggestions.length) % suggestions.length,
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      this.handleSelect(suggestions[activeSuggestion]);
    } else if (e.key === "Escape") {
      this.clearSuggestions();
    }
  };

  clearSuggestions = () => {
    this.fetchSuggestions.cancel();
    this.requestId++;
    this.setState({ suggestions: [], loading: false });
  };

  findAddressComponent = (components, type) => {
    return _.find(components, (component) => {
      return component.types.find((componentType) => {
        return componentType === type;
      });
    });
  };

  handleSelect = (placePrediction) => {
    const address = placePrediction.text.text;
    this.clearSuggestions();
    this.setState({ address });

    // toPlace() carries the session token over to this fetchFields call,
    // which ends the autocomplete session.
    const place = placePrediction.toPlace();
    this.sessionToken = null;

    place
      .fetchFields({
        fields: ["addressComponents", "formattedAddress", "location"],
      })
      .then(() => {
        const components = place.addressComponents || [];
        let city = this.findAddressComponent(components, "locality");

        // Barry case when locality is "Street"
        if (city && city.longText === "Street") {
          city = null;
        }

        if (!city) {
          city = this.findAddressComponent(
            components,
            "administrative_area_level_2"
          );
        }

        if (!city) {
          city = this.findAddressComponent(components, "sublocality");
        }

        if (!city) {
          city = this.findAddressComponent(
            components,
            "administrative_area_level_1"
          );
        }

        if (!city) {
          city = { longText: "" };
        }

        let addressObj = {
          address: address,
          city: city.longText,
          lat: place.location.lat(),
          lng: place.location.lng(),
        };
        this.setState({
          address: address,
          error: false,
          addressObj: addressObj,
        });
        this.props.onSelect(addressObj);
      })
      .catch((err) => {
        console.log(err);
        this.setState({ error: true });
      });
  };

  simpleDialogClose = () => {
    this.setState({ simpleDialogOpen: false, addressName: "" });
  };

  simpleDialogOpen = () => {
    this.setState({ simpleDialogOpen: true });
  };

  handleAddressNameChange = (e) => {
    let { value } = e.target;
    this.setState({ addressName: value });
  };

  addToFavouriteAddresses = () => {
    let address = {
      name: this.state.addressName,
      address: { ...this.state.addressObj },
    };

    axios.post(`addresses`, address).then((res) => {
      this.simpleDialogClose();
      address.id = res.data.address.id;
      this.props.addToAddressesList(address);
    });
  };

  onDragOver = (e) => {
    this.dragging(true);
    e.stopPropagation();
    e.preventDefault();
  };

  onDrop = (e) => {
    let data = e.dataTransfer.getData("text");
    data = JSON.parse(data);
   

    if (data.address && data.city) {
      this.setState({
        address: data.address,
        dragging: false,
        addressObj: data,
      });
     this.props.onSelect(data);
    }
  };

  dragging = (dragging) => {
    if (this.state.draggin !== dragging) this.setState({ dragging });
  };

  render() {
    let input = "";
    let { classes, label, t } = this.props;
    let error = false;

    if (!label) label = t("adres");

    if (this.state.address && !this.state.addressObj.city) {
      label = t("wybierzadres");
      error = true;
    }

    let inputStyle = {};

    if (this.state.error) {
      inputStyle = {
        backgroundColor: "red",
        color: "white",
      };
    }

    if (this.state.dragging) {
      inputStyle = {
        backgroundColor: "#a2cf6e",
      };
    }



    if (this.state.ready) {
      input = (
        <div className={classes.container}>
          <TextField
            error={error ? true : false}
            label={label}
            value={this.state.address}
            margin="dense"
            fullWidth
            placeholder="Szukaj adresu ..."
            autoComplete="off"
            inputProps={{ className: "location-search-input" }}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onBlur={this.clearSuggestions}
            className={this.props.inputClass}
            onDragOver={this.onDragOver}
            onDrop={this.onDrop}
            onDragLeave={() => this.dragging(false)}
            style={inputStyle}
          />
          {this.state.addressObj.city && this.props.addToAddressesList && (
            <Tooltip title="Dodaj adres do ulubionych" placement="top">
              <Button
                size="small"
                color="primary"
                onClick={this.simpleDialogOpen}
              >
                <Icon>add_box</Icon>
              </Button>
            </Tooltip>
          )}
          <Paper square className={classes.paper}>
            {this.state.loading && <div>Loading...</div>}
            {this.state.suggestions.map((placePrediction, index) => {
              const active = index === this.state.activeSuggestion;
              const className = active
                ? "suggestion-item--active"
                : "suggestion-item";
              // inline style for demonstration purpose
              const style = active
                ? { backgroundColor: "#fafafa", cursor: "pointer" }
                : { backgroundColor: "#ffffff", cursor: "pointer" };
              return (
                <MenuItem
                  key={placePrediction.placeId}
                  className={className}
                  style={style}
                  onMouseEnter={() =>
                    this.setState({ activeSuggestion: index })
                  }
                  // keep focus on the input so onBlur doesn't clear the list first
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => this.handleSelect(placePrediction)}
                >
                  {placePrediction.text.text}
                </MenuItem>
              );
            })}
          </Paper>
        </div>
      );
    }

    let dialogContent = (
      <React.Fragment>
        <DialogContent>
          <TextField
            label="Podaj nazwę"
            value={this.state.addressName}
            margin="dense"
            fullWidth
            onChange={this.handleAddressNameChange}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={this.simpleDialogClose}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={this.addToFavouriteAddresses}
          >
            Dodaj
          </Button>
        </DialogActions>
      </React.Fragment>
    );

    return (
      <React.Fragment>
        <SimpleDialog
          open={this.state.simpleDialogOpen}
          content={dialogContent}
          onClose={this.simpleDialogClose}
        />
        {input}
      </React.Fragment>
    );
  }
}

const styles = (theme) => ({
  container: {
    flexGrow: 1,
    position: "relative",
    overflow: "visible",
    display: "flex",
  },
  paper: {
    position: "absolute",
    zIndex: 9999999999,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
    top: "50px",
  },
});

export default scriptLoader(
  "https://maps.googleapis.com/maps/api/js?key=" +
    config.gmaps_api_key +
    "&libraries=places&loading=async"
)(withStyles(styles)(withTranslation("translations")(AddressInput)));
